// src/stores/NoteStore.js - Zustand Store for Customer Notes
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useNoteStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Notes indexed by customer ID
    // Structure: { customerId: { notes: [], count: 0, loading: false } }
    notesByCustomer: {},

    // Currently selected note (for editing)
    selectedNote: null,
    selectedNoteId: null,

    // Granular saving flags (canonical pattern)
    creatingNote: false,
    savingNote: false,
    deletingNote: false,

    // Connection state
    isConnected: false,

    // Internal flags
    _initialized: false,

    // ================================
    // SOCKET OPERATIONS
    // ================================

    /**
     * Load notes for a customer
     * @param {string} customerId - Customer ID
     * @param {object} options - { includeTags }
     */
    loadCustomerNotes: (customerId, options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('NoteStore: Socket service not initialized');
        return;
      }

      if (!customerId) {
        console.error('NoteStore: Customer ID is required');
        return;
      }

      const { includeTags = true } = options;

      console.log('NoteStore: Loading notes for customer:', customerId);

      // Set loading state for this customer
      set(state => ({
        notesByCustomer: {
          ...state.notesByCustomer,
          [customerId]: {
            ...state.notesByCustomer[customerId],
            loading: true
          }
        }
      }));

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_CUSTOMER_NOTES, {
        customer_id: customerId,
        include_tags: includeTags
      });
    },

    /**
     * Create a new note
     * @param {object} noteData - Note data
     */
    createNote: (noteData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('NoteStore: Socket service not initialized');
        return;
      }

      if (!noteData.customer_id || !noteData.content) {
        console.error('NoteStore: Customer ID and content are required');
        return;
      }

      console.log('NoteStore: Creating note for customer:', noteData.customer_id);

      set({ creatingNote: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_NOTE, noteData);
    },

    /**
     * Update an existing note
     * @param {string} noteId - Note ID
     * @param {object} updates - Fields to update
     */
    updateNote: (noteId, updates) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('NoteStore: Socket service not initialized');
        return;
      }

      if (!noteId) {
        console.error('NoteStore: Note ID is required');
        return;
      }

      console.log('NoteStore: Updating note:', noteId);

      set({ savingNote: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_NOTE, {
        note_id: noteId,
        ...updates
      });
    },

    /**
     * Delete a note
     * @param {string} noteId - Note ID
     */
    deleteNote: (noteId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('NoteStore: Socket service not initialized');
        return;
      }

      if (!noteId) {
        console.error('NoteStore: Note ID is required');
        return;
      }

      console.log('NoteStore: Deleting note:', noteId);

      set({ deletingNote: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_NOTE, {
        note_id: noteId
      });
    },

    /**
     * Pin or unpin a note
     * @param {string} noteId - Note ID
     * @param {boolean} pinned - Whether to pin
     */
    pinNote: (noteId, pinned = true) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('NoteStore: Socket service not initialized');
        return;
      }

      if (!noteId) {
        console.error('NoteStore: Note ID is required');
        return;
      }

      console.log('NoteStore:', pinned ? 'Pinning' : 'Unpinning', 'note:', noteId);

      set({ savingNote: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.PIN_NOTE, {
        note_id: noteId,
        pinned
      });
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Get notes for a specific customer
     */
    getNotesForCustomer: (customerId) => {
      const { notesByCustomer } = get();
      return notesByCustomer[customerId]?.notes || [];
    },

    /**
     * Get loading state for a customer
     */
    isLoadingNotesForCustomer: (customerId) => {
      const { notesByCustomer } = get();
      return notesByCustomer[customerId]?.loading || false;
    },

    /**
     * Get total count for a customer
     */
    getTotalNotesForCustomer: (customerId) => {
      const { notesByCustomer } = get();
      return notesByCustomer[customerId]?.count || 0;
    },

    /**
     * Select a note for editing
     */
    selectNote: (note) => {
      set({
        selectedNote: note,
        selectedNoteId: note?.id || null,
      });
    },

    /**
     * Clear selected note
     */
    clearSelection: () => {
      set({
        selectedNote: null,
        selectedNoteId: null,
      });
    },

    // Backwards compatibility alias
    clearSelectedNote: () => {
      get().clearSelection();
    },

    /**
     * Start a new note (set up editor for new note creation)
     * Uses 'new' as selectedNoteId to indicate new note mode
     */
    startNewNote: () => {
      set({
        selectedNote: null,
        selectedNoteId: 'new',
      });
    },

    /**
     * Clear notes for a customer (e.g., when navigating away)
     */
    clearNotesForCustomer: (customerId) => {
      set(state => {
        const updated = { ...state.notesByCustomer };
        delete updated[customerId];
        return { notesByCustomer: updated };
      });
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setNotesLoaded: (customerId, data) => {
      console.log('NoteStore: Notes loaded for customer:', customerId);

      set(state => ({
        notesByCustomer: {
          ...state.notesByCustomer,
          [customerId]: {
            notes: data.notes || [],
            count: data.count || 0,
            loading: false
          }
        }
      }));
    },

    _addNote: (note) => {
      const customerId = note.customer_id;
      if (!customerId) return;

      set(state => {
        const customerNotes = state.notesByCustomer[customerId] || { notes: [], count: 0, loading: false };
        return {
          notesByCustomer: {
            ...state.notesByCustomer,
            [customerId]: {
              ...customerNotes,
              notes: [note, ...customerNotes.notes],
              count: customerNotes.count + 1
            }
          }
        };
      });
    },

    _updateNote: (updatedNote) => {
      const customerId = updatedNote.customer_id;
      if (!customerId) return;

      set(state => {
        const customerNotes = state.notesByCustomer[customerId];
        if (!customerNotes) return state;

        return {
          notesByCustomer: {
            ...state.notesByCustomer,
            [customerId]: {
              ...customerNotes,
              notes: customerNotes.notes.map(note =>
                note.id === updatedNote.id ? updatedNote : note
              )
            }
          },
          selectedNote: state.selectedNote?.id === updatedNote.id ? updatedNote : state.selectedNote
        };
      });
    },

    _removeNote: (data) => {
      // Backend sends 'id', not 'note_id'
      const noteId = data.note_id || data.id;
      const { customer_id } = data;

      set(state => {
        const updatedNotesByCustomer = { ...state.notesByCustomer };

        // If customer_id provided, use it directly
        if (customer_id && state.notesByCustomer[customer_id]) {
          const customerNotes = state.notesByCustomer[customer_id];
          updatedNotesByCustomer[customer_id] = {
            ...customerNotes,
            notes: customerNotes.notes.filter(note => note.id !== noteId),
            count: Math.max(0, customerNotes.count - 1)
          };
        } else {
          // Search through all customers to find the note
          for (const [custId, customerData] of Object.entries(state.notesByCustomer)) {
            const noteIndex = customerData.notes.findIndex(n => n.id === noteId);
            if (noteIndex !== -1) {
              updatedNotesByCustomer[custId] = {
                ...customerData,
                notes: customerData.notes.filter(note => note.id !== noteId),
                count: Math.max(0, customerData.count - 1)
              };
              break;
            }
          }
        }

        return {
          notesByCustomer: updatedNotesByCustomer,
          selectedNote: state.selectedNote?.id === noteId ? null : state.selectedNote,
          selectedNoteId: state.selectedNoteId === noteId ? null : state.selectedNoteId
        };
      });
    },

    _setNotesLoadFailed: (customerId) => {
      if (!customerId) return;

      set(state => ({
        notesByCustomer: {
          ...state.notesByCustomer,
          [customerId]: {
            ...state.notesByCustomer[customerId],
            loading: false
          }
        }
      }));
    },

    /**
     * Add a tag to a note (called by tag event listener)
     */
    _addTagToNote: (noteId, tagging) => {
      set(state => {
        // Find which customer has this note
        const updatedNotesByCustomer = { ...state.notesByCustomer };
        let updatedSelectedNote = state.selectedNote;

        for (const [customerId, customerData] of Object.entries(state.notesByCustomer)) {
          const noteIndex = customerData.notes.findIndex(n => n.id === noteId);
          if (noteIndex !== -1) {
            const note = customerData.notes[noteIndex];
            const existingTags = note.tags || [];

            // Check if tag already exists
            if (!existingTags.some(t => t.tag?.id === tagging.tag?.id || t.tag_id === tagging.tag_id)) {
              const updatedNote = {
                ...note,
                tags: [...existingTags, tagging]
              };

              updatedNotesByCustomer[customerId] = {
                ...customerData,
                notes: customerData.notes.map((n, i) => i === noteIndex ? updatedNote : n)
              };

              // Update selectedNote if it's the same note
              if (state.selectedNote?.id === noteId) {
                updatedSelectedNote = updatedNote;
              }
            }
            break;
          }
        }

        return { notesByCustomer: updatedNotesByCustomer, selectedNote: updatedSelectedNote };
      });
    },

    /**
     * Remove a tag from a note (called by tag event listener)
     */
    _removeTagFromNote: (noteId, tagId) => {
      set(state => {
        const updatedNotesByCustomer = { ...state.notesByCustomer };
        let updatedSelectedNote = state.selectedNote;

        for (const [customerId, customerData] of Object.entries(state.notesByCustomer)) {
          const noteIndex = customerData.notes.findIndex(n => n.id === noteId);
          if (noteIndex !== -1) {
            const note = customerData.notes[noteIndex];
            const updatedNote = {
              ...note,
              tags: (note.tags || []).filter(t =>
                t.tag?.id !== tagId && t.tag_id !== tagId && t.id !== tagId
              )
            };

            updatedNotesByCustomer[customerId] = {
              ...customerData,
              notes: customerData.notes.map((n, i) => i === noteIndex ? updatedNote : n)
            };

            // Update selectedNote if it's the same note
            if (state.selectedNote?.id === noteId) {
              updatedSelectedNote = updatedNote;
            }
            break;
          }
        }

        return { notesByCustomer: updatedNotesByCustomer, selectedNote: updatedSelectedNote };
      });
    },

    // ================================
    // INTERNAL METHODS
    // ================================

    _getSocketManager: () => {
      return {
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null
      };
    },

    _initialize: (socketManager) => {
      console.log('NoteStore: Initializing with socket manager');

      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        isConnected: true,
        _initialized: true
      });
    },

    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },

    _handleDisconnect: () => {
      set({
        creatingNote: false,
        savingNote: false,
        deletingNote: false,
      });
    },

    _handleAuthReset: () => {
      console.log('NoteStore: Resetting all state due to auth reset');

      set({
        notesByCustomer: {},
        selectedNote: null,
        selectedNoteId: null,
        creatingNote: false,
        savingNote: false,
        deletingNote: false,
        isConnected: false,
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP
// ================================

// Track pending requests to map responses to customers
let pendingNoteRequests = new Map();

export const initializeNoteStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }

  console.log('NoteStore: Setting up socket event listeners');

  const { universalHandler } = socketManager;

  // Intercept loadCustomerNotes to track pending requests
  const originalLoadCustomerNotes = useNoteStore.getState().loadCustomerNotes;
  useNoteStore.setState({
    loadCustomerNotes: (customerId, options = {}) => {
      pendingNoteRequests.set('current', customerId);
      originalLoadCustomerNotes(customerId, options);
    }
  });

  // Set up event listeners
  const noteEvents = [
    SOCKET_EVENTS.CUSTOMER_NOTES_LOADED,
    SOCKET_EVENTS.NOTE_CREATED,
    SOCKET_EVENTS.NOTE_UPDATED,
    SOCKET_EVENTS.NOTE_DELETED,
    SOCKET_EVENTS.NOTE_PINNED,
    SOCKET_EVENTS.NOTE_UNPINNED,
    SOCKET_EVENTS.GET_CUSTOMER_NOTES_FAILED,
    SOCKET_EVENTS.CREATE_NOTE_FAILED,
    SOCKET_EVENTS.UPDATE_NOTE_FAILED,
    SOCKET_EVENTS.DELETE_NOTE_FAILED,
    SOCKET_EVENTS.PIN_NOTE_FAILED,
    // Tag events - NoteStore handles tag updates for notes
    SOCKET_EVENTS.TAG_ADDED_TO_ENTITY,
    SOCKET_EVENTS.TAG_REMOVED_FROM_ENTITY
  ];

  noteEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      const store = useNoteStore.getState();

      switch (eventName) {
        case SOCKET_EVENTS.CUSTOMER_NOTES_LOADED:
          const customerId = pendingNoteRequests.get('current');
          if (customerId) {
            store._setNotesLoaded(customerId, data);
          }
          pendingNoteRequests.delete('current');
          break;

        case SOCKET_EVENTS.NOTE_CREATED:
          useNoteStore.setState({ creatingNote: false });
          store._addNote(data);
          break;

        case SOCKET_EVENTS.NOTE_UPDATED:
        case SOCKET_EVENTS.NOTE_PINNED:
        case SOCKET_EVENTS.NOTE_UNPINNED:
          useNoteStore.setState({ savingNote: false });
          store._updateNote(data);
          break;

        case SOCKET_EVENTS.NOTE_DELETED:
          useNoteStore.setState({ deletingNote: false });
          store._removeNote(data);
          break;

        case SOCKET_EVENTS.GET_CUSTOMER_NOTES_FAILED:
          const failedCustomerId = pendingNoteRequests.get('current');
          store._setNotesLoadFailed(failedCustomerId);
          pendingNoteRequests.delete('current');
          console.error('NoteStore: Failed to load notes:', data?.message);
          break;

        case SOCKET_EVENTS.CREATE_NOTE_FAILED:
          useNoteStore.setState({ creatingNote: false });
          console.error('NoteStore: Create failed:', data?.message);
          break;

        case SOCKET_EVENTS.UPDATE_NOTE_FAILED:
        case SOCKET_EVENTS.PIN_NOTE_FAILED:
          useNoteStore.setState({ savingNote: false });
          console.error('NoteStore: Update failed:', data?.message);
          break;

        case SOCKET_EVENTS.DELETE_NOTE_FAILED:
          useNoteStore.setState({ deletingNote: false });
          console.error('NoteStore: Delete failed:', data?.message);
          break;

        case SOCKET_EVENTS.TAG_ADDED_TO_ENTITY:
          // Only handle note-related tag events
          if (data.taggable_type === 'note') {
            console.log('NoteStore: Tag added to note:', data.taggable_id);
            store._addTagToNote(data.taggable_id, data.tagging);
          }
          break;

        case SOCKET_EVENTS.TAG_REMOVED_FROM_ENTITY:
          // Only handle note-related tag events
          if (data.taggable_type === 'note') {
            console.log('NoteStore: Tag removed from note:', data.taggable_id);
            store._removeTagFromNote(data.taggable_id, data.tag_id);
          }
          break;
      }
    });
  });

  // Set up auth reset and socket-disconnected listeners
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useNoteStore.getState()._handleAuthReset();
      pendingNoteRequests.clear();
    });
    window.addEventListener('socket-disconnected', () => {
      useNoteStore.getState()._handleDisconnect();
    });
  }

  // Initialize the store
  useNoteStore.getState()._initialize(socketManager);

  console.log('NoteStore: Socket event listeners initialized');
};

export default useNoteStore;
